import { Col, Row } from 'antd';
import Empty from 'components/Empty';
import ModalConfirm, { useModalConfirmRef } from 'components/Modal/ModalConfirm';
import Spin from 'components/Spin';
import Text from 'components/Text';
import userActions from 'features/user/services/actions';
import userSelectors from 'features/user/services/selectors';
import { IBillWaitingTicketResItem, IStaffTicketResItem } from 'features/user/services/types/ticket';
import { useAppDispatch } from 'store/hooks';
import { styled } from 'styled-components';
import ButtonMergeTicket from './ButtonMergeTicket';
import ButtonRemoveTicket from './ButtonRemoveTicket';
import TicketItem from './Item';
import userApis from 'features/user/services/apis';
import toast from 'utils/toast';
import { get } from 'lodash';
import CallStaffsModal, { useCallStaffsModalRef } from './CallStaffsModal';
import shopSelectors from 'services/shop/selectors';

type Props = {
  mode: 'staff' | 'ticket' | 'default';
  v2?: boolean;
};
const TicketWaitingList = ({ v2 }: Props) => {
  const modalConfirm = useModalConfirmRef();
  const callStaffsModalRef = useCallStaffsModalRef();
  const dispatch = useAppDispatch();
  const data = userSelectors.getBillWaitingTickets();
  const loading = userSelectors.getBillWaitingTicketLoading();
  const isClockIn = userSelectors.staff.isClockIn();
  const activeRemoveTicket = userSelectors.activeRemoveTicket();
  const enableMergeTicket = userSelectors.enableMergeTicket();
  const mergeTicketsList = userSelectors.mergeTicketsList();
  const allSetting = shopSelectors.data.allSetting();
  const onToggle = () => {
    dispatch(userActions.activeRemoveTicket(!activeRemoveTicket));
  };

  const onRemoveTicket = (record: IBillWaitingTicketResItem) => () => {
    modalConfirm.current?.open(`Do you like to delete the ticket #${record.ticketNumber}?`, () => {
      dispatch(userActions.removePendingTicket(record.billId));
    });
  };

  const handleRevertToWaiting = async (id: string) => {
    try {
      const res = await userApis.revertWorkingOnToWaiting(id);
      if (res.data.data) {
        toast.success('Revert ticket to waiting successfully');
        dispatch(userActions.getBillWaitingTicket.fetch());
        dispatch(userActions.getWaitingList.fetch());
      }
    } catch (error) {
      const msg = get(error, 'response.data.message');
      toast.error(msg || 'Revert ticket to waiting failed');
    }
  };

  const openModalConfirmRevertToWaiting = (id: string) => {
    modalConfirm.current?.open('Do you like to move this ticket back to “Still Waiting”?', () =>
      handleRevertToWaiting(id)
    );
  };

  const onSelectMergeTicket = (o: IBillWaitingTicketResItem) => () => {
    dispatch(userActions.selectMergeTicket(o.billId));
  };

  const handleCallStaffs = (staffs: IStaffTicketResItem[]) => {
    if (staffs?.length) {
      callStaffsModalRef.current?.open(staffs);
    }
  };

  const RowData = () => {
    if (loading) return <Spin />;

    return data.length > 0 ? (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: '8px',
        }}
      >
        {data.map((o) => (
          <TicketItem
            key={o.billId}
            data={o}
            viewOnly={!isClockIn}
            removeMode={activeRemoveTicket}
            onRemoveTicket={onRemoveTicket(o)}
            mergeTicketsList={mergeTicketsList}
            enableMergeTicket={enableMergeTicket}
            onSelectMergeTicket={onSelectMergeTicket(o)}
            openModalConfirmRevertToWaiting={openModalConfirmRevertToWaiting}
            handleCallStaffs={handleCallStaffs}
            displayAvatarStaff={allSetting?.displayAvatarStaff}
          />
        ))}
      </div>
    ) : v2 ? null : (
      <Empty description='Empty ticket' centerFloating />
    );
  };
  if (v2) {
    return (
      <Styled className='ticket-label-section'>
        <Row className='top-title' align={'middle'} style={{ gap: '8px' }}>
          <FlexBox>
            <Text
              style={{ flex: 1, fontWeight: '700' }}
              className='label-text'
              variant='H7'
              color='text_5'
            >
              Working On...
            </Text>
            {data.length > 0 ? <ButtonMergeTicket v2 /> : null}
            <div className='text-wrapper'>
              <span className='label-text-number'>{data.length}</span>
              <span className='sub-text'> Tickets</span>
            </div>
          </FlexBox>
        </Row>
        <div className='box-div-scroll'>
          <RowData />
        </div>
        <ModalConfirm ref={modalConfirm} />
        <CallStaffsModal ref={callStaffsModalRef} />
      </Styled>
    );
  }
  return (
    <Styled>
      <Row align={'middle'} style={{ gap: '8px', marginBottom: '0.5rem' }}>
        <Text variant='H7'>In The Progress...</Text>
        <Text style={{ flex: 1 }} />

        {data.length > 0 ? (
          <>
            <ButtonMergeTicket />
            <ButtonRemoveTicket type='ticket' active={activeRemoveTicket} onToggle={onToggle} />
          </>
        ) : (
          <div style={{ height: '2.5rem' }}></div>
        )}
      </Row>
      <div className='box-div-scroll'>
        <RowData />
      </div>
      <ModalConfirm ref={modalConfirm} />
    </Styled>
  );
};

export default TicketWaitingList;

const Styled = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  padding-top: 0.5rem;
  padding-bottom: 0;
  background: #8defab4d;
  .box-div-scroll {
    flex: 1;
    margin: 0 -1rem;
    padding: 0 1rem;
    background: transparent;
    padding-right: 1rem;
    padding-top: 1rem;
    overflow-y: scroll;
    scrollbar-width: none; /* Firefox 64 */
    &::-webkit-scrollbar {
      width: 0px;
    }
  }

  &.ticket-label-section {
    padding-top: 0;
    background: transparent;
    .top-title {
      background: #fff;
      margin: 0 -1rem;
      padding: 0 1rem;
      height: 64px;
      min-height: 64px;
      max-height: 64px;
      overflow: hidden;
      .label-text {
        font-size: 22px;
        text-transform: uppercase;
      }
    }
  }
`;

const FlexBox = styled.div`
  gap: 8px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  .text-wrapper {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .label-text-number {
    color: #1d2129;
    font-size: 32px;
    font-weight: 600;
    line-height: 32px;
    .sub-text {
      color: #505050;
      font-size: 14px;
      font-weight: 600;
      line-height: normal;
    }
  }
`;
